import React, { memo, useContext, useCallback, useEffect } from 'react'
import HotelYouMayAlsoLikeTile from './HotelYouMayAlsoLikeTile'
import TourYouMayAlsoLikeTile from './TourYouMayAlsoLikeTile'
import BedbankYouMayAlsoLikeTile from './BedbankYouMayAlsoLikeTile'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import TrackingContext from 'contexts/trackingContext'
import offerPageURL from 'lib/offerPageURL'
import { fireInteractionEvent } from 'api/googleTagManager'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import BundledHotelYouMayAlsoLikeTile from './BundledHotelYouMayAlsoLikeTile'
import { isTourV2Offer } from 'lib/offerTypes'
import TourV2YouMayAlsoLikeTile from 'components/OfferPage/TourV2/YouMayAlsoLike/TourV2YouMayAlsoLikeTile'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { useInView } from 'react-intersection-observer'
import { productImpression } from 'analytics/enhanced-ecommerce'
import { productImpressionObject } from 'analytics/eventDefinitions'
import GeoContext from 'contexts/geoContext'

interface Props {
  offer: App.AnyOffer;
  position: number;
  onClick?: (offer: App.AnyOffer, position: number) => void;
  linkQuery?: string;
  filters?: App.OfferListFilters;
}

function YouMayAlsoLikeTile({ offer, position, onClick: callback, linkQuery, filters }: Props) {
  const tracking = useContext(TrackingContext)
  const onClick = useCallback(() => {
    if (tracking) {
      fireInteractionEvent({
        ...tracking,
        action: 'Click',
        value: position,
      })
    }
    callback?.(offer, position)
  }, [tracking, offer, position, callback])

  const [inViewRef, inView] = useInView({ triggerOnce: true })
  const { currentCurrency } = useContext(GeoContext)
  const listId = tracking?.listId ?? 'unknown_ymal_list'
  const listName = tracking?.listName ?? 'unknown_ymal_list'
  useEffect(() => {
    if (inView) {
      productImpression(
        currentCurrency,
        [productImpressionObject(offer, listName, position)],
        {
          listId,
          listName,
        },
        tracking?.lere?.version,
      )
    }
  }, [inView, currentCurrency, listId, listName, offer, position, tracking?.lere])

  return (
    <ClickableLink
      to={`${offerPageURL(offer)}?${linkQuery}`}
      onClick={onClick}
      ref={inViewRef}
    >
      {(offer.type === OFFER_TYPE_HOTEL && offer.bundledWithFlightsOnly && (
        <BundledHotelYouMayAlsoLikeTile offer={offer} />
      ))}
      {((offer.type === OFFER_TYPE_HOTEL && !offer.bundledWithFlightsOnly) ||
        offer.type === OFFER_TYPE_ALWAYS_ON ||
        offer.type === OFFER_TYPE_LAST_MINUTE
      ) && (
        <HotelYouMayAlsoLikeTile offer={offer}/>
      )}
      {offer.type === OFFER_TYPE_TOUR && offer.lowestPricePackage && (
        <ErrorBoundary fallback={() => null} reportErrors><TourYouMayAlsoLikeTile offer={offer} /></ErrorBoundary>
      )}
      {offer.type === OFFER_TYPE_BED_BANK && (
        <BedbankYouMayAlsoLikeTile offer={offer} filters={filters}/>
      )}
      {isTourV2Offer(offer) && (
        <TourV2YouMayAlsoLikeTile offer={offer} />
      )}
    </ClickableLink>
  )
}

export default memo(YouMayAlsoLikeTile)
